import { BrowserModule } from '@angular/platform-browser';
import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptorsFromDi,
} from '@angular/common/http';
import { AppComponent } from './app.component';
import { DashboardService } from './services/dashboard.service';
import { LayoutService } from './services/layout.service';
import { OrganisationSelectionComponent } from './components/organisation-selection/organisation-selection.component';
import { DashboardSelectionComponent } from './components/dashboard-selection/dashboard-selection.component';
import { ReportsSelectionComponent } from './components/reports-selection/reports-selection.component';
import { DateRangeComponent } from './components/sidebar/date-range/date-range.component';
import { SecurityGuardService } from './services/security-guard.service';
import { ChartModule } from './highcharts/chart.module';
import { FormsModule } from '@angular/forms';
import { HeadersService } from './services/headers.service';
import { ChartsService } from './services/charts.service';
import { ReportsModule } from './components/reports/reports.module';
import { ChartTileModule } from './components/tiles/chart-tile/chart-tile.module';
import { SingletonServicesModule } from './services/singleton-services.module';
import { RouterModule } from '@angular/router';
import { DashboardsModule } from './components/dashboards/dashboards.module';
import { SidebarModule } from './components/sidebar/sidebar.module';
import { MyAccountModule } from './components/my-account/my-account.module';
import { SpinnerModule } from './components/spinner/spinner.module';
import { AnalyseComponent } from './components/analyse/analyse-component/analyse.component';
import { ComponentsService } from './services/components.service';
import { EditAnalyseModalComponent } from './components/analyse/edit-analyse-modal/edit-analyse-modal.component';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { OptionsModule } from './components/options/options.module';
import { DataSetsComponent } from './components/data-sets/data-sets.component';
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';
import { NgbToastModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../environments/environment';
import { NoCacheInterceptor } from './interceptors/no-cache.interceptor';
import { ServiceMessageService } from './services/service-message.service';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { of, switchMap } from 'rxjs';
import { SetupService } from './services/setup.service';
import { UserpilotService } from './services/userpilot.service';
import { SecurityService } from './services/security.service';
import { ToastContainerComponent } from './components/toast-container/toast-container.component';

export function initializeApp(): Promise<boolean> {
    const setupService = inject(SetupService);
    const userPilotService = inject(UserpilotService);
    const securityService = inject(SecurityService);

    userPilotService.initialize();

    return new Promise<boolean>((resolve, reject) => {
        securityService
            .isAuthenticated()
            .pipe(
                switchMap((response: boolean) => {
                    if (response) {
                        return setupService.ensureAppIsSetUp();
                    }
                    securityService.auth();
                    return of(false);
                })
            )
            .subscribe({
                next: (result) => {
                    resolve(result);
                },
                error: (error) => {
                    reject(error);
                },
            });
    });
}

@NgModule({
    declarations: [
        AppComponent,
        OrganisationSelectionComponent,
        DashboardSelectionComponent,
        ReportsSelectionComponent,
        DateRangeComponent,
        AnalyseComponent,
        EditAnalyseModalComponent,
        DataSetsComponent,
        UnauthorizedComponent,
    ],
    imports: [
        BrowserModule,
        CommonModule,
        AppRoutingModule,
        ChartTileModule,
        ChartModule,
        DashboardsModule,
        FormsModule,
        MyAccountModule,
        OptionsModule,
        ReportsModule,
        RouterModule,
        SidebarModule,
        SingletonServicesModule,
        SpinnerModule,
        NgbTooltipModule,
        NgbToastModule,
        NgTemplateOutlet,
        ToastContainerComponent,
    ],
    providers: [
        DashboardService,
        LayoutService,
        SecurityService,
        provideAppInitializer(initializeApp),
        SecurityGuardService,
        HeadersService,
        ChartsService,
        ComponentsService,
        {
            provide: ApplicationInsights,
            useValue: new ApplicationInsights({
                config: {
                    instrumentationKey: environment
                        ? environment.instrumentation_key
                        : '',
                },
            }),
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: NoCacheInterceptor,
            multi: true,
        },
        ServiceMessageService,
        provideHttpClient(withInterceptorsFromDi()),
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}

declare global {
    export interface Navigator {
        msSaveBlob(blob: Blob, fileName: string): void;
    }
}
